import React from 'react';
import { motion } from 'framer-motion';
// Import the CORRECT Lottie component
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import servicesData from '../data/servicesData'; // Ensure this file imports JSONs and assigns them to lottieData

// Import necessary icons used in servicesData (ensure these match your data)
import { FaCheck } from 'react-icons/fa'; // Icon for detail points

// Component for individual service section
const ServiceSection = ({ service, index }) => {
    const isOdd = index % 2 !== 0; // Determine layout order

    return (
        // Using key on the section helps React differentiate sibling sections if needed
        <section key={service.title} className={`py-12 md:py-16 ${isOdd ? 'bg-white' : 'bg-gray-50'}`}>
            <div className="container mx-auto px-4">
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center`}>
                    {/* Lottie Animation Column */}
                    <motion.div
                        className={`flex justify-center items-center ${isOdd ? 'md:order-last' : ''}`} // Change order for alternating layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
                    >
                        {/* Use DotLottieReact component with src prop */}
                        {service.lottieSrcUrl && service.lottieSrcUrl.includes('.lottie') && ( // Basic check for valid URL
                            <DotLottieReact
                                src={service.lottieSrcUrl} // Use the .lottie URL from data
                                loop
                                autoplay
                                // Increased size: Adjusted width and maxWidth
                                style={{ width: '90%', maxWidth: '480px', height: 'auto' }}
                            />
                        )}
                    </motion.div>

                    {/* Text Content Column */}
                    <motion.div
                        initial={{ opacity: 0, x: isOdd ? 50 : -50 }} // Animate from different side based on order
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                    >
                        <div className="inline-flex items-center justify-center w-12 h-12 mb-5 rounded-full bg-arcdom-gold text-white">
                            {/* Render the icon component passed in props */}
                            {React.isValidElement(service.icon) ? React.cloneElement(service.icon, { size: 28 }) : null}
                        </div>
                        <h3 className="text-3xl font-bold text-arcdom-navy mb-4">{service.title}</h3>
                        <p className="text-gray-600 mb-6">{service.description}</p>
                        {/* Ensure service.details is an array before mapping */}
                        {Array.isArray(service.details) && (
                            <ul className="space-y-2">
                                {service.details.map((point, idx) => (
                                    <li key={idx} className="flex items-start">
                                        <FaCheck className="w-4 h-4 mr-3 mt-1 text-arcdom-gold flex-shrink-0" />
                                        <span className="text-gray-700">{point}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};


const ServicesPage = () => {
    return (
        <div className="bg-[#eceeF1] min-h-screen">
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="pt-32 pb-16 bg-gradient-to-r from-arcdom-navy via-arcdom-dark-gray to-arcdom-navy text-center"
            >
                <div className="container mx-auto px-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Expertise</h1>
                    <p className="text-lg text-arcdom-taupe max-w-2xl mx-auto">Comprehensive solutions in property development and construction.</p>
                </div>
            </motion.div>

            {/* Render each service using the ServiceSection component */}
            {servicesData.map((service, index) => (
                <ServiceSection key={index} service={service} index={index} />
            ))}

            {/* Optional: Add a concluding section or CTA */}
            <section className="py-16 bg-arcdom-dark-gray text-center">
                <div className="container mx-auto px-4">
                    <h3 className="text-2xl font-semibold text-white mb-4">Ready to Build Your Vision?</h3>
                    <p className="text-arcdom-taupe mb-6">Let's discuss how our passion and expertise can bring your project to life.</p>
                    {/* Consider using Link component if /contact is an internal route */}
                    <a
                        href="/contact"
                        className="inline-block px-8 py-3 bg-arcdom-gold text-white font-semibold rounded hover:bg-opacity-90 transition-colors"
                    >
                        Get in Touch
                    </a>
                </div>
            </section>

        </div>
    );
};

export default ServicesPage;
